import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faBars,
  faCalendarCheck,
  faChevronDown,
  faHouse,
  faMoon,
  faShop,
  faSun,
  faUser,
  faUserShield,
  faUsers,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useEffect, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'
import { applyDocumentDirection } from '../i18n'
import { useAuth, hasAnyRole } from '../auth/AuthContext'
import { homePathForRoles, isStaffRoles } from '../auth/roles'
import { useTheme } from '../theme/ThemeContext'
import { BrandLogo } from './BrandLogo'
import { HeaderCartButton } from './HeaderCartButton'
import { FlagAustria, FlagSaudi } from './LangFlags'
import './PublicLayout.css'
import '../pages/DashboardAdmin.css'

function PartnerSalonLinks() {
  const q = useQuery({
    queryKey: ['public-salon-index'],
    queryFn: () => apiFetch<{ data: Array<{ slug: string; name?: string }> }>('/api/v1/public/sitemap-salons'),
    staleTime: 60_000,
  })
  const rows = q.data?.data ?? []
  if (rows.length === 0) return null

  return (
    <>
      {rows.map((salon) => (
        <li key={salon.slug}>
          <Link to={`/${salon.slug}`}>{salon.name || salon.slug}</Link>
        </li>
      ))}
    </>
  )
}

export type DashNavItem = {
  to: string
  label: string
  icon: IconDefinition
  end?: boolean
}

export type DashNavGroup = {
  label?: string
  items: DashNavItem[]
}

type MenuKey = 'lang' | 'account' | null

function SmartDropdown({
  id,
  label,
  open,
  onOpen,
  onClose,
  children,
  align = 'start',
  className = '',
}: {
  id: MenuKey
  label: React.ReactNode
  open: boolean
  onOpen: (id: MenuKey) => void
  onClose: () => void
  children: React.ReactNode
  align?: 'start' | 'end'
  className?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<number | null>(null)

  const clearClose = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  const scheduleClose = () => {
    clearClose()
    closeTimer.current = window.setTimeout(() => onClose(), 160)
  }

  useEffect(() => () => clearClose(), [])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  return (
    <div
      className={`smart-dd ${open ? 'open' : ''} align-${align} ${className}`.trim()}
      ref={ref}
      onMouseEnter={() => {
        clearClose()
        onOpen(id)
      }}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        className="smart-dd-trigger"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => (open ? onClose() : onOpen(id))}
      >
        {label}
        <FontAwesomeIcon icon={faChevronDown} className="chev" />
      </button>
      {open && (
        <div className="smart-dd-panel" role="menu" onMouseEnter={clearClose}>
          {children}
        </div>
      )}
    </div>
  )
}

export function PublicLayout({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [menu, setMenu] = useState<MenuKey>(null)

  // Keep customers on the booking wizard after login from header.
  const loginHref = location.pathname.startsWith('/book')
    ? `/login?next=${encodeURIComponent(`${location.pathname}${location.search || '?resume=1'}`)}`
    : '/login'

  useEffect(() => {
    setMobileOpen(false)
    setMenu(null)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!mobileOpen) return
    const prev = document.body.style.overflow
    document.body.classList.add('nav-drawer-lock')
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.classList.remove('nav-drawer-lock')
      document.body.style.overflow = prev
    }
  }, [mobileOpen])

  const switchLocale = (locale: string) => {
    void i18n.changeLanguage(locale)
    localStorage.setItem('ilia.locale', locale)
    applyDocumentDirection(locale)
    setMenu(null)
  }

  const closeMenus = () => setMenu(null)

  return (
    <div className={`public-shell${mobileOpen ? ' is-nav-open' : ''}`}>
      <header className="site-header">
        <div className="container header-inner">
          <BrandLogo onClick={closeMenus} />

          {mobileOpen ? (
            <button
              type="button"
              className="nav-drawer-backdrop"
              aria-label={t('common.close')}
              onClick={() => setMobileOpen(false)}
            />
          ) : null}

          <nav className={`main-nav ${mobileOpen ? 'open' : ''}`} aria-label="Main">
            <NavLink to="/" end onClick={() => setMobileOpen(false)}>
              {t('nav.home')}
            </NavLink>
            <NavLink to="/leistungen" onClick={() => setMobileOpen(false)}>
              {t('nav.services')}
            </NavLink>
            <NavLink to="/club" onClick={() => setMobileOpen(false)}>
              {t('nav.subscriptions')}
            </NavLink>
            <NavLink to="/filialen" onClick={() => setMobileOpen(false)}>
              {t('nav.explore')}
            </NavLink>
            <NavLink to="/shop" onClick={() => setMobileOpen(false)}>
              {t('nav.shop')}
            </NavLink>
            <NavLink to="/contact" onClick={() => setMobileOpen(false)}>
              {t('nav.contact')}
            </NavLink>
            <NavLink to="/subscribe" onClick={() => setMobileOpen(false)} className="nav-for-salons">
              {t('nav.forSalons')}
            </NavLink>

            <div className="mobile-only-actions">
              <Link to="/styles" className="btn btn-ghost" onClick={() => setMobileOpen(false)}>
                {t('nav.styles')}
              </Link>
              <Link to="/book" className="btn btn-primary" onClick={() => setMobileOpen(false)}>
                {t('nav.book')}
              </Link>
              {user ? (
                <>
                  {isStaffRoles(user.roles) ? (
                    <Link className="btn btn-ghost" to={homePathForRoles(user.roles)} onClick={() => setMobileOpen(false)}>
                      {t('nav.dashboard')}
                    </Link>
                  ) : (
                    <Link className="btn btn-ghost" to="/account" onClick={() => setMobileOpen(false)}>
                      {t('dashboard.customer')}
                    </Link>
                  )}
                  <button className="btn btn-primary" type="button" onClick={logout}>
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link className="btn btn-ghost" to={loginHref} onClick={() => setMobileOpen(false)}>
                  {t('nav.login')}
                </Link>
              )}
            </div>
          </nav>

          <div className="header-actions">
            <div className="lang-flag-switch mobile-only" role="group" aria-label={t('nav.language')}>
              <button
                type="button"
                className={`lang-flag-chip${i18n.language === 'de' ? ' active' : ''}`}
                onClick={() => switchLocale('de')}
                aria-label="Deutsch"
                title="Deutsch"
              >
                <FlagAustria />
                <span>DE</span>
              </button>
              <button
                type="button"
                className={`lang-flag-chip${i18n.language === 'ar' ? ' active' : ''}`}
                onClick={() => switchLocale('ar')}
                aria-label="العربية"
                title="العربية"
              >
                <FlagSaudi />
                <span>AR</span>
              </button>
            </div>
            <HeaderCartButton />
            <button
              type="button"
              className="icon-btn"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
              title={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            >
              <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} />
            </button>

            <SmartDropdown
              id="lang"
              className="desktop-only"
              label={
                <>
                  {i18n.language === 'ar' ? <FlagSaudi /> : <FlagAustria />}
                  <span>{i18n.language === 'ar' ? 'AR' : 'DE'}</span>
                </>
              }
              open={menu === 'lang'}
              onOpen={setMenu}
              onClose={closeMenus}
              align="end"
            >
              <button type="button" role="menuitem" className={`lang-menu-item${i18n.language === 'de' ? ' active' : ''}`} onClick={() => switchLocale('de')}>
                <FlagAustria />
                <span className="lang-menu-copy">
                  <strong>Deutsch</strong>
                  <span>Österreich</span>
                </span>
              </button>
              <button type="button" role="menuitem" className={`lang-menu-item${i18n.language === 'ar' ? ' active' : ''}`} onClick={() => switchLocale('ar')}>
                <FlagSaudi />
                <span className="lang-menu-copy">
                  <strong>العربية</strong>
                  <span>السعودية</span>
                </span>
              </button>
            </SmartDropdown>

            {user ? (
              <SmartDropdown
                id="account"
                className="desktop-only"
                label={
                  <>
                    <FontAwesomeIcon icon={faUser} />
                    <span className="hide-sm">{user.firstName}</span>
                  </>
                }
                open={menu === 'account'}
                onOpen={setMenu}
                onClose={closeMenus}
                align="end"
              >
                {isStaffRoles(user.roles) ? (
                  <Link to={homePathForRoles(user.roles)} role="menuitem" onClick={closeMenus}>
                    <strong>{t('nav.dashboard')}</strong>
                    <span>{user.email}</span>
                  </Link>
                ) : (
                  <Link to="/account" role="menuitem" onClick={closeMenus}>
                    <strong>{t('dashboard.customer')}</strong>
                    <span>{user.email}</span>
                  </Link>
                )}
                <button type="button" role="menuitem" onClick={() => { logout(); closeMenus() }}>
                  <strong>{t('nav.logout')}</strong>
                </button>
              </SmartDropdown>
            ) : (
              <Link className="btn btn-primary desktop-only" to={loginHref}>
                {t('nav.login')}
              </Link>
            )}

            <button
              className="nav-toggle icon-btn"
              type="button"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
              aria-expanded={mobileOpen}
            >
              <FontAwesomeIcon icon={mobileOpen ? faXmark : faBars} />
            </button>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="site-footer">
        <div className="container footer-grid">
          <div className="footer-brand">
            <BrandLogo />
            <p>{t('footer.tagline')}</p>
          </div>

          <div>
            <h3>{t('footer.visit')}</h3>
            <ul className="footer-list">
              <li><Link to="/filialen">{t('nav.explore')}</Link></li>
              <li><Link to="/leistungen">{t('nav.services')}</Link></li>
              <li><Link to="/styles">{t('nav.styles')}</Link></li>
              <li><Link to="/book">{t('nav.book')}</Link></li>
              <li><Link to="/club">{t('club.nav')}</Link></li>
              <li><Link to="/shop">{t('nav.shop')}</Link></li>
            </ul>
          </div>

          <div>
            <h3>{t('footer.platform')}</h3>
            <ul className="footer-list">
              <li><a href="/#plans">{t('nav.pricing')}</a></li>
              <li><a href="/#features">{t('nav.features')}</a></li>
              <li><Link to="/subscribe">{t('nav.subscribe')}</Link></li>
              <li><Link to="/faq">{t('nav.faq')}</Link></li>
              <PartnerSalonLinks />
            </ul>
          </div>

          <div>
            <h3>{t('footer.legal')}</h3>
            <ul className="footer-list">
              <li><Link to="/datenschutz">{t('footer.privacy')}</Link></li>
              <li><Link to="/agb">{t('footer.terms')}</Link></li>
              <li><Link to="/impressum">{t('footer.impressum')}</Link></li>
              <li><Link to="/contact">{t('nav.contact')}</Link></li>
            </ul>
          </div>
        </div>
        <div className="container footer-bottom">
          <p>© {new Date().getFullYear()} {t('brand')}. {t('footer.rights')}</p>
          <p className="footer-meta">Kindberg · Krieglach · Steiermark · Austria</p>
        </div>
      </footer>
    </div>
  )
}

export function DashboardLayout({
  title,
  children,
  navItems,
  navGroups,
  bottomNav,
}: {
  title: string
  children: React.ReactNode
  navItems?: DashNavItem[]
  navGroups?: DashNavGroup[]
  bottomNav?: DashNavItem[]
}) {
  const { t, i18n } = useTranslation()
  const { user, logout } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const location = useLocation()
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const hasCustomNav = Boolean(navItems?.length || navGroups?.length)
  const hasBottomNav = Boolean(bottomNav?.length)

  useEffect(() => {
    setMobileNavOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!mobileNavOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileNavOpen(false)
    }
    document.addEventListener('keydown', onKey)
    document.body.classList.add('dash-nav-lock')
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.classList.remove('dash-nav-lock')
    }
  }, [mobileNavOpen])

  const switchLocale = (locale: string) => {
    void i18n.changeLanguage(locale)
    localStorage.setItem('ilia.locale', locale)
    applyDocumentDirection(locale)
  }

  const defaultCustomerNav = hasAnyRole(user, ['ROLE_CUSTOMER']) && !hasCustomNav

  const navClass = (to: string, end?: boolean) => {
    const [pathPart, hashPart] = to.split('#')
    const path = pathPart || '/'
    const pathMatch = end
      ? location.pathname === path
      : location.pathname === path || location.pathname.startsWith(path + '/')
    if (hashPart !== undefined && to.includes('#')) {
      const currentHash = (location.hash || '#home').replace(/^#/, '') || 'home'
      const pathOk = end ? location.pathname === path : pathMatch
      const want = hashPart || 'home'
      return pathOk && currentHash === want ? 'active' : undefined
    }
    // Path-based salon dashboard: /salon/dashboard, /salon/calendar, …
    if (path.startsWith('/salon/') || path === '/salon') {
      if (end) {
        return location.pathname === path || location.pathname === '/salon' || location.pathname === '/salon/dashboard'
          ? 'active'
          : undefined
      }
      return location.pathname === path ? 'active' : undefined
    }
    if (path === '/account') {
      return pathMatch && (!location.hash || location.hash === '#' || location.hash === '#overview')
        ? 'active'
        : undefined
    }
    if (path === '/admin' && end) {
      const currentHash = (location.hash || '#overview').replace(/^#/, '')
      return pathMatch && (currentHash === 'overview' || !location.hash) ? 'active' : undefined
    }
    return pathMatch ? 'active' : undefined
  }

  const closeMobileNav = () => setMobileNavOpen(false)

  const renderNavLink = (item: DashNavItem) => (
    <NavLink key={item.to} to={item.to} end={item.end} className={() => navClass(item.to, item.end)}>
      <FontAwesomeIcon icon={item.icon} className="dash-nav-icon" />
      {item.label}
    </NavLink>
  )

  return (
    <div className={`dash-shell ${mobileNavOpen ? 'is-nav-open' : ''} ${hasBottomNav ? 'has-bottom-nav' : ''}`}>
      <div className="dash-mobile-bar">
        <div className="dash-mobile-brand">
          <button
            type="button"
            className="dash-menu-toggle icon-btn"
            aria-label={mobileNavOpen ? t('common.close') : t('common.menu')}
            aria-expanded={mobileNavOpen}
            aria-controls="dash-side-nav"
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            <FontAwesomeIcon icon={mobileNavOpen ? faXmark : faBars} />
          </button>
        </div>
        <div className="dash-mobile-center">
          <p className="dash-mobile-title">{title}</p>
          {user?.firstName ? (
            <p className="dash-mobile-welcome">
              {t('dashboard.welcome')}, {user.firstName}
            </p>
          ) : null}
        </div>
        <div className="dash-mobile-actions">
          <button
            type="button"
            className="icon-btn dash-mobile-theme"
            onClick={toggleTheme}
            aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            title={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
          >
            <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} />
          </button>
        </div>
      </div>

      {mobileNavOpen && (
        <button
          type="button"
          className="dash-nav-backdrop"
          aria-label={t('common.close')}
          onClick={closeMobileNav}
        />
      )}

      <aside className="dash-aside" id="dash-side-nav">
        <div className="dash-aside-brand">
          <BrandLogo />
          <p className="dash-title">{title}</p>
          <button
            type="button"
            className="dash-aside-close icon-btn"
            aria-label={t('common.close')}
            onClick={closeMobileNav}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <nav className="dash-nav" aria-label={title} onClick={closeMobileNav}>
          {!hasCustomNav && (
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : undefined)}>
              <FontAwesomeIcon icon={faHouse} className="dash-nav-icon" />
              {t('nav.home')}
            </NavLink>
          )}
          {!hasCustomNav && hasAnyRole(user, ['ROLE_SUPER_ADMIN', 'ROLE_PLATFORM_MANAGER']) && (
            <NavLink to="/admin" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              <FontAwesomeIcon icon={faUserShield} className="dash-nav-icon" />
              {t('dashboard.superAdmin')}
            </NavLink>
          )}
          {!hasCustomNav && hasAnyRole(user, ['ROLE_SALON_OWNER', 'ROLE_BRANCH_MANAGER', 'ROLE_ACCOUNTANT']) && (
            <NavLink to="/salon" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              <FontAwesomeIcon icon={faShop} className="dash-nav-icon" />
              {t('dashboard.salon')}
            </NavLink>
          )}
          {!hasCustomNav && hasAnyRole(user, ['ROLE_BARBER', 'ROLE_RECEPTIONIST']) && (
            <NavLink to="/employee" className={({ isActive }) => (isActive ? 'active' : undefined)}>
              <FontAwesomeIcon icon={faUsers} className="dash-nav-icon" />
              {t('dashboard.employee')}
            </NavLink>
          )}
          {navGroups?.map((group, gi) => (
            <div key={group.label ?? `g-${gi}`} className="dash-nav-group">
              {group.label ? <p className="dash-nav-group-label">{group.label}</p> : null}
              {group.items.map(renderNavLink)}
            </div>
          ))}
          {!navGroups?.length && navItems?.map(renderNavLink)}
          {defaultCustomerNav && (
            <NavLink to="/account" end className={({ isActive }) => (isActive ? 'active' : undefined)}>
              <FontAwesomeIcon icon={faUser} className="dash-nav-icon" />
              {t('dashboard.customer')}
            </NavLink>
          )}
          {!hasCustomNav && (
            <>
              <NavLink to="/book" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                <FontAwesomeIcon icon={faCalendarCheck} className="dash-nav-icon" />
                {t('nav.book')}
              </NavLink>
              <NavLink to="/shop" className={({ isActive }) => (isActive ? 'active' : undefined)}>
                <FontAwesomeIcon icon={faShop} className="dash-nav-icon" />
                {t('nav.shop')}
              </NavLink>
            </>
          )}
        </nav>
        <div className="dash-aside-foot">
          {user && (
            <div className="dash-user-chip">
              <span className="dash-user-avatar" aria-hidden>
                {(user.firstName?.[0] ?? '').toUpperCase()}
                {(user.lastName?.[0] ?? '').toUpperCase()}
              </span>
              <div>
                <strong>{user.firstName} {user.lastName}</strong>
                <em>{user.email}</em>
              </div>
            </div>
          )}
          <div className="dash-aside-mobile-tools">
            <div className="lang-switch">
              <button type="button" className={i18n.language === 'de' ? 'active' : ''} onClick={() => switchLocale('de')}>DE</button>
              <button type="button" className={i18n.language === 'ar' ? 'active' : ''} onClick={() => switchLocale('ar')}>AR</button>
            </div>
          </div>
          <button className="btn btn-ghost dash-logout" type="button" onClick={logout}>
            {t('nav.logout')}
          </button>
        </div>
      </aside>
      <div className="dash-main">
        <header className="dash-top">
          <div>
            <h1>{title}</h1>
            <p>{t('dashboard.welcome')}{user ? `, ${user.firstName}` : ''}</p>
          </div>
          <div className="dash-top-actions">
            <button
              type="button"
              className="icon-btn"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
              title={theme === 'dark' ? t('nav.lightMode') : t('nav.darkMode')}
            >
              <FontAwesomeIcon icon={theme === 'dark' ? faSun : faMoon} />
            </button>
            <div className="lang-switch">
              <button type="button" className={i18n.language === 'de' ? 'active' : ''} onClick={() => switchLocale('de')}>DE</button>
              <button type="button" className={i18n.language === 'ar' ? 'active' : ''} onClick={() => switchLocale('ar')}>AR</button>
            </div>
            <button className="btn btn-ghost dash-top-logout" type="button" onClick={logout} aria-label={t('nav.logout')}>
              <span className="dash-logout-label">{t('nav.logout')}</span>
            </button>
          </div>
        </header>
        <div className="dash-content">{children}</div>
      </div>

      {hasBottomNav ? (
        <nav className="dash-bottom-nav" aria-label={title}>
          {bottomNav!.map((item) => {
            const active = navClass(item.to, item.end) === 'active'
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={active ? 'active' : undefined}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
              </NavLink>
            )
          })}
        </nav>
      ) : null}
    </div>
  )
}
